import { Component, ReactNode } from 'react';
import { connect } from 'react-redux';

import { CommentType, MessageType, RootState } from 'types';
import { VoidInterface } from 'types/functions';
import { checkDifferent } from 'utils/formHandlers';
import { twclsx } from 'utils/twclsx';

import Comment from './Comment';
import Icon from './Icon';
import Label from './Label';

interface FormGroupProps {
    className?: string;
    withActions?: boolean;
    span?: number;
    required?: boolean;
    children?: ReactNode;
    language?: string;
    comments?: CommentType[];
    label?: MessageType | string | undefined;
    horizontal?: boolean;
    editAction?: VoidInterface;
    deleteAction?: VoidInterface;
}

interface FormGroupState {
    language: string;
    label: MessageType | string | undefined;
    comments: CommentType | null;
}

class FormGroup extends Component<FormGroupProps, FormGroupState> {
    constructor(props: FormGroupProps) {
        super(props);
        this.state = {
            language: 'en',
            label: props.label || '',
            comments: null,
        };
    }

    static getDerivedStateFromProps(props: FormGroupProps, state: FormGroupState) {
        if (
            props.language !== state.language ||
            checkDifferent(props?.comments, state?.comments) ||
            checkDifferent(props?.label, state?.label)
        ) {
            return {
                comments: props?.comments,
                label: props?.label,
                language: props.language,
            };
        }
        return null;
    }

    render() {
        const { className, comments, language, required, children, span, withActions, editAction, deleteAction } =
            this.props;
        const label = this.state.label || '';
        return (
            <div className={twclsx(`col-span-${span || '1'}`, className || 'mt-3 mb-1')}>
                {label && (
                    <div className="flex items-center">
                        <Label>
                            {typeof label === 'object' ? label?.[language as keyof typeof label] : label}{' '}
                            <span className="text-red-500"> {required && '*'}</span>{' '}
                        </Label>
                        {withActions ? (
                            <div className="flex gap-2 items-center ms-auto mb-1" style={{ right: 0 }}>
                                <div
                                    className="w-4 h-4 flex justify-center items-center cursor-pointer"
                                    onClick={() => {
                                        editAction?.();
                                    }}
                                >
                                    <Icon className="w-6 h-6">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
                                        <path d="M13.5 6.5l4 4" />
                                    </Icon>
                                </div>
                                <div
                                    className="w-4 h-4 flex justify-center items-center cursor-pointer"
                                    onClick={() => {
                                        deleteAction?.();
                                    }}
                                >
                                    <Icon className="w-6 h-6">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <path d="M4 7l16 0" />
                                        <path d="M10 11l0 6" />
                                        <path d="M14 11l0 6" />
                                        <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                                        <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                                    </Icon>
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
                {children}

                <div className={this.props?.horizontal ? 'h-5' : ''}>
                    {comments?.map((comment: CommentType) => {
                        return (
                            <Comment key={comment.uuid} type={comment.type}>
                                {comment.text}
                            </Comment>
                        );
                    })}
                </div>
            </div>
        );
    }
}

const formGroupStateToProps = (state: RootState) => ({});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formGroupDispatchToProps = () => ({});

export default connect(formGroupStateToProps, formGroupDispatchToProps)(FormGroup);
